import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Plus, MoreVertical, Edit, Copy, Trash2, Send, Presentation,
  FileText, Calculator, Loader2, CheckCircle, Clock, AlertCircle,
  XCircle, Eye, EyeOff, Calendar, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// STATUS CONFIGURATION
// ============================================================================

const STATUS_CONFIG = {
  DRAFT: { 
    label: 'Draft', 
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    icon: FileText
  },
  PRESENTED: { 
    label: 'Presented', 
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    icon: Presentation
  },
  SENT: { 
    label: 'Sent', 
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    icon: Send
  },
  SIGNED: { 
    label: 'Signed', 
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: CheckCircle
  },
  ACCEPTED: { 
    label: 'Accepted', 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    icon: CheckCircle
  },
  DECLINED: { 
    label: 'Declined', 
    color: 'bg-red-100 text-red-700 border-red-300',
    icon: XCircle
  },
  EXPIRED: { 
    label: 'Expired', 
    color: 'bg-gray-100 text-gray-500 border-gray-300',
    icon: Clock
  },
};

// ============================================================================
// ESTIMATE ROW COMPONENT
// ============================================================================

const EstimateRow = ({ 
  estimate, 
  onEdit, 
  onPresent, 
  onSend, 
  onDuplicate, 
  onDelete,
  onViewSigned
}) => {
  const statusConfig = STATUS_CONFIG[estimate.status] || STATUS_CONFIG.DRAFT;
  const StatusIcon = statusConfig.icon;
  
  // Use counts from API list response
  const visiblePages = estimate.customer_visible_pages ?? 
    (estimate.pages?.filter(p => p.customer_visible)?.length || 0);
  const totalPages = estimate.total_pages ?? estimate.pages?.length ?? visiblePages;

  return (
    <div 
      className="group flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm transition-all"
      data-testid={`estimate-row-${estimate.id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* Status Icon */}
        <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
          <StatusIcon className="h-5 w-5" />
        </div>
        
        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-slate-900 dark:text-white truncate">
              {estimate.title || 'Untitled Estimate'}
            </span>
            <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
              {statusConfig.label}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            <span className="font-mono text-xs">{estimate.estimate_number}</span>
            <span className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {visiblePages}/{totalPages} pages visible
            </span>
            {estimate.total > 0 && (
              <span className="font-medium text-slate-700 dark:text-slate-300">
                ${estimate.total.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        
        {/* Date */}
        <div className="text-sm text-slate-500 dark:text-slate-400 hidden md:block">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(estimate.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 ml-4">
        {/* Quick Actions (visible on hover) */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onEdit(estimate)}
            title="Edit Estimate"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onPresent(estimate)}
            title="Present to Customer"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30"
            data-onboard="present-estimate-btn"
          >
            <Presentation className="h-4 w-4" />
          </Button>
        </div>
        
        {/* More Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onEdit(estimate)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPresent(estimate)}>
              <Presentation className="h-4 w-4 mr-2" />
              Present to Customer
            </DropdownMenuItem>
            {(estimate.status === 'SIGNED' || estimate.status === 'ACCEPTED') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onViewSigned(estimate)}>
                  <FileText className="h-4 w-4 mr-2" />
                  View Agreement
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onSend(estimate)}>
              <Send className="h-4 w-4 mr-2" />
              Send to Customer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(estimate)}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onDelete(estimate)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN ESTIMATES TAB COMPONENT
// ============================================================================

export const EstimatesTab = ({ jobId, project, user, onRefresh }) => {
  const navigate = useNavigate();
  
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newEstimateTitle, setNewEstimateTitle] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Fetch estimates for this job
  const fetchEstimates = useCallback(async () => {
    if (!jobId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs/${jobId}/estimates`);
      setEstimates(response.data || []);
    } catch (error) {
      console.error('Error fetching estimates:', error);
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  // Create new estimate with default pages
  const handleCreateEstimate = async () => {
    if (!jobId) return;
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/estimates`, {
        title: newEstimateTitle || `Estimate for ${project?.customer_name || 'Customer'}`
      });
      
      const newEstimate = response.data;
      toast.success('Estimate created successfully');
      setShowNewDialog(false);
      setNewEstimateTitle('');
      
      // Navigate to editor
      navigate(`/workspace/jobs/${jobId}/estimates/${newEstimate.id}/edit`);
    } catch (error) {
      console.error('Error creating estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create estimate');
    } finally {
      setCreating(false);
    }
  };

  // Edit estimate
  const handleEdit = (estimate) => {
    navigate(`/workspace/jobs/${jobId}/estimates/${estimate.id}/edit`);
  };

  // Present estimate
  const handlePresent = (estimate) => {
    navigate(`/workspace/jobs/${jobId}/estimates/${estimate.id}/present`);
  };

  // View Signed Document
  const handleViewSigned = (estimate) => {
    navigate(`/workspace/jobs/${jobId}/estimates/${estimate.id}/present?signed=true`);
  };

  // Send estimate (creates a sent version snapshot)
  const handleSend = async (estimate) => {
    setSendConfirm(estimate);
  };
  
  const confirmSend = async () => {
    if (!sendConfirm) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/estimates/${sendConfirm.id}/send`);
      toast.success('Estimate sent successfully! A version snapshot has been created.');
      setSendConfirm(null);
      fetchEstimates();
    } catch (error) {
      console.error('Error sending estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to send estimate');
    } finally {
      setSending(false);
    }
  };

  // Duplicate estimate
  const handleDuplicate = async (estimate) => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/estimates/${estimate.id}/duplicate`);
      toast.success('Estimate duplicated successfully');
      fetchEstimates();
      
      // Optionally navigate to the new estimate
      // navigate(`/workspace/jobs/${jobId}/estimates/${response.data.id}/edit`);
    } catch (error) {
      console.error('Error duplicating estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate estimate');
    }
  };

  // Delete estimate
  const handleDelete = (estimate) => {
    setDeleteConfirm(estimate);
  };
  
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await axios.delete(`${API}/jobs/${jobId}/estimates/${deleteConfirm.id}`);
      toast.success('Estimate deleted');
      setDeleteConfirm(null);
      fetchEstimates();
    } catch (error) {
      console.error('Error deleting estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete estimate');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading estimates...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="estimates-tab">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-500" />
                Estimates
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Create and manage estimates for this project
              </p>
            </div>
            <Button 
              onClick={() => setShowNewDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-estimate-btn"
              data-onboard="new-estimate-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Estimate
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Estimates List */}
      {estimates.length === 0 ? (
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Calculator className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Estimates Yet</h3>
              <p className="text-slate-500 mb-4">
                Create your first estimate for this project
              </p>
              <Button 
                onClick={() => setShowNewDialog(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Estimate
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {estimates.map(estimate => (
            <EstimateRow
              key={estimate.id}
              estimate={estimate}
              onEdit={handleEdit}
              onPresent={handlePresent}
              onSend={handleSend}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              onViewSigned={handleViewSigned}
            />
          ))}
        </div>
      )}

      {/* New Estimate Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Estimate</DialogTitle>
            <DialogDescription>
              This will create a new draft estimate with default pages (Cover, Details, Signature, Terms & Conditions).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Estimate Title</Label>
              <Input
                value={newEstimateTitle}
                onChange={(e) => setNewEstimateTitle(e.target.value)}
                placeholder={`Estimate for ${project?.customer_name || 'Customer'}`}
                className="mt-1"
              />
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-2">Default pages included:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li>Cover Page (visible to customer)</li>
                <li>Estimate Details (visible to customer)</li>
                <li>Signature Page (visible to customer)</li>
                <li>Terms & Conditions (visible, locked at end)</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEstimate} 
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Estimate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimate?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Confirmation */}
      <AlertDialog open={!!sendConfirm} onOpenChange={() => setSendConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Estimate to Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create an immutable &quot;sent&quot; version snapshot of the estimate. The current draft will be locked and a new version will be created.
              <br /><br />
              <span className="font-medium">Note:</span> Email delivery is not yet configured. The snapshot will be saved for future reference.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmSend}
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send & Create Snapshot
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EstimatesTab;
